// Libs
import { FieldType, ViewModelBase, ApiResult } from "@shoothill/core";
import { action, computed, observable, runInAction } from "mobx";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import moment from "moment";
import validator from "validator";

// App
import { AppUrls } from "AppUrls";
import { StoresInstance } from "Globals/Stores";
import { SaveStore } from "Globals/Stores/Domain";

// Models
import { FamilyAddressModel } from "Globals/Models/Domain/FormSections/PersonalDetails/FamilyAddressModel";
import { FamilyAddressDTO } from "Globals/Models/DTO/FamilyAddressDTO";

// ViewModels
import PersonalDetailsViewModel from "./PersonalDetailsViewModel";

export default class FamilyAddressViewModel extends ViewModelBase<FamilyAddressModel> {
    private saveStore: SaveStore = StoresInstance.Domain.SaveStore;

    constructor(familyAddressModel?: FamilyAddressModel) {
        super(familyAddressModel ? familyAddressModel : new FamilyAddressModel());
    }

    @action
    public stash = async (personalDetails: PersonalDetailsViewModel): Promise<void> => {
        if ((await this.isModelValid()) && this.isModelDirty) {
            let addressModel = new FamilyAddressModel();
            addressModel.fromDto(this.model);
            let addressViewModel = new FamilyAddressViewModel(addressModel);

            runInAction(() => {
                personalDetails.familyAddressViewModels.push(addressViewModel);
                // triggers the auto-save.
                personalDetails.setDirty("id", true);
            });

            this.model.resetModel();
        }
    };

    @action
    public saveFromStash = async (personalDetailId: string): Promise<void> => {
        try {
            this.setIsLoading(true);
            this.saveStore.setIsSaving(true);

            this.model.personalDetailId = personalDetailId;

            let upsertFamilyAddress: FamilyAddressDTO = this.model.toDto();
            const apiResult = await this.Post<FamilyAddressDTO>(AppUrls.Server.ApplicationForm.Upsert.FamilyAddress, upsertFamilyAddress);

            if (apiResult.wasSuccessful) {
                this.model.fromDto(apiResult.payload);
                console.info("FamilyAddress:Upsert-Successful");
                this.setModelDirty(false);
                return Promise.resolve();
            } else {
                console.error("FamilyAddress:Upsert-Error", apiResult.errors);
                return Promise.reject(apiResult.errors);
            }
        } catch (exception) {
            // Set server validation error messages.
            console.log("Could not save from Stash.");
            console.log(exception);
        } finally {
            this.saveStore.setIsSaving(false);
            this.setIsLoading(false);
        }
    };

    @action
    public delete = async (personalDetails: PersonalDetailsViewModel): Promise<void> => {
        try {
            // If it's just in the stash, remove it.
            if (!this.model.getValue("personalDetailId")) {
                runInAction(() => {
                    let index = personalDetails.familyAddressViewModels.findIndex((vm) => vm.getValue("id") == this.model.id);
                    if (index > -1) {
                        personalDetails.familyAddressViewModels.splice(index, 1);
                    }
                });
            } else {
                this.setIsLoading(true);
                this.saveStore.setIsSaving(true);

                this.model.isDeleted = true;

                let upsertFamilyAddress: FamilyAddressDTO = this.model.toDto();
                const apiResult = await this.Post<FamilyAddressDTO>(AppUrls.Server.ApplicationForm.Upsert.FamilyAddress, upsertFamilyAddress);

                if (apiResult.wasSuccessful) {
                    this.model.fromDto(apiResult.payload);
                    console.info("FamilyAddress:Delete-Successful");

                    // Add / remove address into array
                    let addressModel = new FamilyAddressModel();
                    addressModel.fromDto(apiResult.payload);
                    let addressViewModel = new FamilyAddressViewModel(addressModel);
                    const filtered = personalDetails.familyAddressViewModels.filter((a) => a.model.isDeleted == false);
                    runInAction(() => {
                        personalDetails.familyAddressViewModels = filtered;
                    });

                    this.model.resetModel(); // Clear form
                    this.setModelDirty(false);
                    return Promise.resolve();
                } else {
                    console.error("FamilyAddress:Delete-Error", apiResult.errors);
                    return Promise.reject(apiResult.errors);
                }
            }
        } catch (exception) {
            // Set server validation error messages.
        } finally {
            this.saveStore.setIsSaving(false);
            this.setIsLoading(false);
        }
    };

    @computed
    public get isModelDirty(): boolean {
        let dirty = false;
        Object.keys(this.model).forEach((field) => {
            if (this.model.getDirty(field as keyof FieldType<FamilyAddressModel>)) {
                dirty = true;
            }
        });
        return dirty;
    }
    @action
    public setModelDirty(value: boolean): void {
        Object.keys(this.model).forEach((field) => {
            this.model.setDirty(field as keyof FieldType<FamilyAddressModel>, value);
        });
    }

    public async isFieldValid(fieldName: keyof FieldType<FamilyAddressModel>, value: any): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);
        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }
    public afterUpdate: undefined;
    public beforeUpdate: undefined;
}
